import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';
import { formatWeekdayDay } from '../../../../utils/dateLocale';

interface Props {
  /** How many dinners the gesture just added. */
  count: number;
  from: string;
  to: string;
  onAdd: () => void;
  onLater: () => void;
}

/**
 * Asked once, right under the dinners "the next 7 days" just added (#722).
 *
 * The grocery list is never changed behind the cook's back. They may already
 * have shopped for this week; items appearing on a list they are standing in a
 * store with is how that list stops being trusted. So the new dinners reach it
 * only on « Ajouter ».
 */
export function GroceryAskCard({ count, from, to, onAdd, onLater }: Props) {
  const { t, i18n } = useTranslation();
  return (
    <View style={styles.card} testID="grocery-ask">
      <Text style={styles.question}>{t('thisWeek.groceryAsk.title', { count })}</Text>
      <Text style={styles.range}>
        {t('thisWeek.extend.range', {
          from: formatWeekdayDay(from, i18n.language),
          to: formatWeekdayDay(to, i18n.language),
        })}
      </Text>
      <View style={styles.buttons}>
        <Pressable
          style={({ pressed }) => [styles.yes, pressed && styles.yesPressed]}
          onPress={onAdd}
          accessibilityRole="button"
          testID="grocery-ask-add"
        >
          <Text style={styles.yesText}>{t('thisWeek.groceryAsk.add')}</Text>
        </Pressable>
        <Pressable
          style={({ pressed }) => [styles.no, pressed && styles.noPressed]}
          onPress={onLater}
          accessibilityRole="button"
          testID="grocery-ask-later"
        >
          <Text style={styles.noText}>{t('thisWeek.groceryAsk.later')}</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    marginHorizontal: 16,
    marginTop: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 16,
    backgroundColor: Paprika.brandTint,
  },
  question: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    color: Paprika.textPrimary,
  },
  range: {
    marginTop: 2,
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textSecondary,
  },
  buttons: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 10,
  },
  yes: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 100,
    backgroundColor: Paprika.primary,
  },
  yesPressed: {
    backgroundColor: Paprika.brandDeep,
  },
  yesText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: Paprika.onPrimary,
  },
  no: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 100,
    backgroundColor: Paprika.surfaceLowest,
  },
  noPressed: {
    backgroundColor: Paprika.surfaceLow,
  },
  noText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
});
